import _ from 'lodash'
import moment from 'moment'
import { constants } from '@adc/ui-core'
import {
  DATA_TYPES,
  OPERATION_TYPES,
} from '../components/dataTable/searchField'
import { parseRangeQuery } from '../components/dataTable/searchField/datePicker'
import { NumberRange } from '../structures/range'
import { DATE_OPTION_TYPES } from '../components/dataTable/constants'
import {
  API_LOCAL_DATE_FORMAT,
  convertDateFormat,
  convertStringTimeToISO,
  formatDateUiToApi,
} from './date'

const { UI_DATE_FORMAT } = constants.statics
const SEARCH_ANALYZER = 'searchAnalyzer'
const DATE_TYPE_GROUP = [
  DATA_TYPES.DATE,
  DATA_TYPES.DATE_AS_ISO,
  DATA_TYPES.DATETIME,
  DATA_TYPES.DATETIME_AS_LOCAL,
]

/**
 * DSL subclause builders
 */
const or = expressions => ({ or: { expressions } })

const and = expressions => ({ and: { expressions } })

const not = expression => ({ not: { expression } })

const exists = field => ({ exists: { field } })

const equals = (fields, values) => ({ equals: { fields, values } })

/**
 * Formats search terms for text-based queries
 */
const formatTerms = terms =>
  terms.reduce((newTerms, term) => {
    const sanitizedTerm = sanitize(term)
    if (!_.isEmpty(sanitizedTerm)) {
      // When search term contains space or dash, we treat it as a whole phrase
      // e.g. "Orange County", "Miami-Dade"
      if (_.includes(sanitizedTerm, ' ') || _.includes(sanitizedTerm, '-')) {
        // Wrap term in quotes to match whole phrase
        newTerms.push(`"${sanitizedTerm}"`)
      } else {
        // Wrap term in '*' to match substring in a word
        newTerms.push(`*${sanitizedTerm}*`)
      }
    }
    return newTerms
  }, [])

/**
 * Remove ! (ES is already ignoring them)
 * Remove periods (to avoid clashing with ES)
 * Trim
 */
const sanitize = term => {
  let result = term.replace(/!/g, '')
  result = result.replace(/\. |\.$/g, ' ')
  return _.trim(result)
}

const buildQueryString = (fields, value) => {
  // Split phrases if value contains quote, otherwise split words
  let terms = _.split(value, /["“”]/)
  if (terms.length === 1) {
    /**
     * Token splitters include: (whitespace) @ + - and .(when preceded by digit)
     *
     * The last separator mentioned is `#.` where # is a digit.  To do this,
     * ideally we regex with a look-behind to find a . preceded by a digit, but
     * since we can't do look-behinds in dumbass firefox we can't go the route.
     *
     * Instead, we first tweak the value so that it looks like a standard
     * separator (like dash) and then split it.  So for example "test1.csv"
     * becomes "test1-csv" and then gets split into 2 words as it should.
     *
     * You might be wondering: "why not just split on period as well?" - well,
     * that's not how ES is configure do work.  "test.csv" for example, is
     * considered one word, whereas "test1.csv" is two words.
     *
     * NOTE: for .# (period then a digit) we also handle that with a simple
     * look ahead in the regex which is supported by firefox.
     */
    const newValue = value.replace(/(?:\d)(\.)/, $0 =>
      $0.length === 2 ? $0.charAt(0) + '-' : $0
    )
    terms = _.split(newValue, /\.(?=\d)|[\s@+-]|,\s+/)
  }
  return { query_string: { fields, value: formatTerms(terms).join(' AND ') } }
}

const buildOrPhraseQuery = (fields, value) => {
  return { query_string: { fields, value: value.join(' OR ') } }
}

const buildOrDateQuery = (clause, fields, values) => {
  const isISO = clause.dataType === DATA_TYPES.DATE_AS_ISO
  const asLocal = clause.dataType === DATA_TYPES.DATETIME_AS_LOCAL
  return or(
    fields.map(field => buildDateQuery(field, values[0], asLocal, isISO))
  )
}

/**
 * Pass in moment span unit e.g. 'week' or 'month' to get start and end dates for that unit of time
 * @param {String} spanUnit
 */
const momentUnitRange =
  spanUnit =>
  (day = moment()) => {
    if (!spanUnit) throw new Error('spanUnit required')
    const [start, end] = ['startOf', 'endOf'].map(fn =>
      day[fn](spanUnit).format('MM/DD/YYYY')
    )

    return `${start} - ${end}`
  }

const determineRelativeDate = value => {
  let s = value
  const today = moment()

  switch (value) {
    case DATE_OPTION_TYPES.TODAY:
      s = today.format(UI_DATE_FORMAT)
      break
    case DATE_OPTION_TYPES.TOMORROW:
      s = today.add(1, 'day').format(UI_DATE_FORMAT)
      break
    case DATE_OPTION_TYPES.YESTERDAY:
      s = today.subtract(1, 'day').format(UI_DATE_FORMAT)
      break
    case DATE_OPTION_TYPES.THIS_WEEK:
      s = momentUnitRange('week')(today)
      break
    case DATE_OPTION_TYPES.NEXT_WEEK:
      s = momentUnitRange('week')(today.add(1, 'week'))
      break
    case DATE_OPTION_TYPES.LAST_WEEK:
      s = momentUnitRange('week')(today.subtract(1, 'week'))
      break
    case DATE_OPTION_TYPES.THIS_MONTH:
      s = momentUnitRange('month')(today)
      break
    case DATE_OPTION_TYPES.NEXT_MONTH:
      s = momentUnitRange('month')(today.add(1, 'month'))
      break
    case DATE_OPTION_TYPES.LAST_MONTH:
      s = momentUnitRange('month')(today.subtract(1, 'month'))
      break
    default:
      throw new Error(`Invalid data value passed: ${value}`)
  }
  return s
}

/**
 * Date search: check for greater than or less than symbols or search within a day range
 */
const buildDateQuery = (field, origValue, asLocal, isISO) => {
  let value = origValue
  if (Object.values(DATE_OPTION_TYPES).includes(origValue)) {
    value = determineRelativeDate(origValue)
  }

  const allDatesAfter = _.startsWith(_.trim(value), '>')
  const allDatesBefore = _.startsWith(_.trim(value), '<')
  const rangeQuery = parseRangeQuery(value)
  const searchFromDay = isISO
    ? convertDateFormat(rangeQuery.from, UI_DATE_FORMAT, API_LOCAL_DATE_FORMAT)
    : formatDateUiToApi(rangeQuery.from, asLocal)

  /**
   * Determines the subsequent day and formats it
   * Have to use "lt next day" because when we range "to" a date, we want all occurrences on that date
   * up to and not including midnight of the next day.
   */
  const subsequentDay = (dateValue, format) =>
    moment.utc(dateValue, UI_DATE_FORMAT).add(1, 'day').utc().format(format)

  const searchDay = isISO
    ? convertDateFormat(value, UI_DATE_FORMAT, API_LOCAL_DATE_FORMAT)
    : formatDateUiToApi(value, asLocal)

  const searchNextDay = isISO
    ? subsequentDay(value, API_LOCAL_DATE_FORMAT)
    : formatDateUiToApi(subsequentDay(value, UI_DATE_FORMAT), asLocal)

  const searchToDay = isISO
    ? subsequentDay(rangeQuery.to, API_LOCAL_DATE_FORMAT)
    : formatDateUiToApi(subsequentDay(rangeQuery.to, UI_DATE_FORMAT), asLocal)

  let gte, gt, lt
  if (allDatesAfter) {
    // We use the selected day here because the before/after queries should include the selected day
    gte = searchDay
  } else if (allDatesBefore) {
    // We use next day here because it searches an exact utc time eg. '2018-05-15T04:00:00Z' and includes the selected day
    lt = searchNextDay
  } else if (rangeQuery.from && rangeQuery.to) {
    gte = searchFromDay
    lt = searchToDay
  } else {
    // Just search for everything within that day eg. '2018-05-14T04:00:00Z' to '2018-05-15T04:00:00Z'
    gte = searchDay
    lt = searchNextDay
  }

  return {
    range: {
      field,
      gte,
      gt,
      lt,
    },
  }
}

const getMultiplePossibleValuesSplitBy = (values = [], separator = ',') => {
  const transformedValues = values.map(value =>
    value.split(separator).map(_.trim)
  )
  const uniqueValues = new Set(transformedValues.flat())
  return Array.from(uniqueValues)
}

/**
 * Single field, exact match, non-Date
 * NOTE: 'equals' can handle a multi-valued array, but just on one field
 * For match on values we have to send keyword only. There are exceptions.
 */
const buildSingleMatchQuery = clause => {
  const isBoolOrNumField = [DATA_TYPES.BOOLEAN, DATA_TYPES.NUMBER].includes(
    clause.dataType
  )
  const searchFieldQuery =
    clause[SEARCH_ANALYZER] || clause[SEARCH_ANALYZER] === ''
      ? `${clause.fields[0]}${clause[SEARCH_ANALYZER]}`
      : `${clause.fields[0]}${isBoolOrNumField ? '' : '.keyword'}`

  // Determine whether the search query requires an exact match
  const isExactMatch = searchFieldQuery.includes('.keyword')
  const clauseValues = isBoolOrNumField
    ? clause.values
    : getMultiplePossibleValuesSplitBy(
        isExactMatch ? clause.values : clause.values.map(_.toLower)
      )

  return {
    equals: {
      fields: searchFieldQuery,
      values: clauseValues,
    },
  }
}

const buildNegateQuery = clause => {
  const { fields } = clause

  const searchFieldQuery = `${fields[0]}.keyword`

  return not({ contains: { fields: searchFieldQuery, values: clause.values } })
}

// TODO: Talk to Steve about lowercase or not here. For the query I am doing, this works like a charm...
const buildGroupedArrayQuery = clause => ({
  match: {
    field: clause.fields[0],
    values: clause.values.join(',').split(','),
  },
})

const buildTimeQuery = clause => ({
  equals: {
    fields: clause.fields[0],
    values: clause.values.map(convertStringTimeToISO),
  },
})

/**
 * This will find matches on fields based on a "set match", meaning it matches the logical set of records represented
 * by the field when stored as "All", "Only" or "All Except" (which leverages is_equals and is_not_equals values)
 * The query breaks down into three parts OR'd together: (All) OR (Is Only: <values>) OR (is not All Except <values>)
 */
const buildSetMatchQuery = clause => {
  const { values, fields } = clause
  const isEqualsField = `${fields[0]}.is_equals`
  const isNotEqualsField = `${fields[0]}.is_not_equals`
  const subParts = Array(3)

  // "All" part
  subParts[0] = and([not(exists(isEqualsField)), not(exists(isNotEqualsField))])
  // "Is Only" part
  subParts[1] = and([
    equals([isEqualsField], values),
    not(exists(isNotEqualsField)),
  ])
  // "not All Except" part
  subParts[2] = and([
    not(exists(isEqualsField)),
    exists(isNotEqualsField),
    /**
     * This last part is to ensure that ALL the values in the query are NOT in the is_not_equals field.
     * The rationale is that if the user is querying on two values (for ex. State: CA, TX) then that means:
     * "find all records that have a logic set that includes CA or TX."  So in the case where the record has
     * is_not_equals of ['AZ', 'CA'], the logical set includes all states except AZ and CA -- so the query for
     * CA or TX should return a hit on this record as this record includes TX. Whereas a query on State: AZ, CA
     * would not return a hit because all the values are in the is_not_equals field (and therefore not in
     * the logical set.)
     */
    not(and(values.map(value => equals([isNotEqualsField], value)))),
  ])

  return or(subParts)
}

/**
 * Generally speaking, a partial matching clause with multiple values is not yet needed
 * so it will not yet be supported until such time we implement that kind of feature.
 *
 * Also, for time being, we will assume multi-field clauses (e.g. All Columns), will always be
 * executed in a partial match fashion -- EVEN IF ALL FIELDS ARE AN EXACT MATCH.  This is a pretty
 * safe assumption as we don't even have that scenario (and probably won't in the long run.)
 *
 * What this allows us to do is avoid nested OR expressions and simply take advantage of match's
 * multi-value support (at least for now).
 */
const buildExpressions = filterBy =>
  filterBy.map(clause => {
    const {
      fields = [],
      values,
      partialMatchFields = [],
      isOrQuery = false,
    } = clause

    if (!_.isArray(fields) || fields.length === 0) {
      throw new Error(
        'Unexpected "fields" value passed, should be an array with length > 0'
      )
    }

    if (!_.isArray(values) || values.length === 0) {
      throw new Error(
        'Unexpected "values" value passed, should be an array with length > 0'
      )
    }

    if (
      (fields.length > 1 ||
        (fields.length === 1 && partialMatchFields.includes(fields[0]))) &&
      values.length > 1 &&
      !isOrQuery
    ) {
      throw new Error(
        'Unexpected attempt to multi-field match and/or partial match on a multi-valued clause.'
      )
    }

    if (isOrQuery) {
      if (DATE_TYPE_GROUP.includes(clause.dataType) && fields.length > 1) {
        return buildOrDateQuery(clause, fields, values)
      }
      return buildOrPhraseQuery(fields, values)
    }

    if (DATE_TYPE_GROUP.includes(clause.dataType)) {
      const isISO = clause.dataType === DATA_TYPES.DATE_AS_ISO
      const asLocal = clause.dataType === DATA_TYPES.DATETIME_AS_LOCAL

      if (fields.length > 1) {
        return or(fields.map(f => buildDateQuery(f, values[0], asLocal, isISO)))
      }

      return buildDateQuery(fields[0], values[0], asLocal, isISO)
    }

    if (clause.preBuiltExpression) {
      return clause.preBuiltExpression
    }

    if (fields.length > 1 || partialMatchFields.includes(fields[0])) {
      // Multiple fields or one with partial match (non-date)
      return buildQueryString(fields, values[0])
    }

    if (clause.dataType === DATA_TYPES.NUMBER_RANGE) {
      return NumberRange.from(values[0]).toSearchCondition(fields[0])
    } else if (clause.dataType === DATA_TYPES.GROUPED_ARRAY) {
      return buildGroupedArrayQuery(clause)
    } else if (clause.dataType === DATA_TYPES.TIME) {
      return buildTimeQuery(clause)
    } else if (clause.setMatch) {
      return buildSetMatchQuery(clause)
    } else if (
      clause.searchOperation === OPERATION_TYPES.COMPARISON.NOT_EQUALS
    ) {
      return buildNegateQuery(clause)
    } else if (
      values.length === 1 &&
      (values[0] === 'EXISTS' || values[0] === 'NOT_EXISTS')
    ) {
      // Handle EXISTS/NOT_EXISTS values from BOOL column type
      return values[0] === 'EXISTS' ? exists(fields[0]) : not(exists(fields[0]))
    }

    return buildSingleMatchQuery(clause)
  })

const DEFAULT_ALL_QUERY = {
  all: {},
}

const buildQuery = filterBy => {
  if (_.isEmpty(filterBy)) {
    return DEFAULT_ALL_QUERY
  }

  const expressions = _.compact(buildExpressions(filterBy))
  return expressions.length === 1 ? expressions[0] : and(expressions)
}

export { buildQuery, determineRelativeDate }
